/**
 * Product Detail Page
 * Direct link to a specific product for ordering
 */

import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ShoppingCart,
  Plus,
  Minus,
  ArrowLeft,
  Truck,
  CheckCircle,
  Shield,
  FileText,
  Phone,
  ExternalLink,
  Package,
  Clock,
  Star,
  Loader2
} from "lucide-react";

export default function ProductDetailPage() {
  const { itemNumber } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await fetch(`${API}/marketplace/products`);
        const data = await res.json();
        const found = data.products?.find(p => p.item_number === itemNumber);
        if (found) {
          setProduct(found);
        } else {
          toast.error("Product not found");
        }
      } catch (error) {
        toast.error("Failed to load product");
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [itemNumber]);

  const handleAddToCart = async () => {
    setAddingToCart(true);
    try {
      const hospitalId = localStorage.getItem("hospital_id") || "demo-hospital";
      const res = await fetch(`${API}/marketplace/cart/${hospitalId}/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          item_number: product.item_number,
          quantity: quantity
        })
      });
      
      if (res.ok) {
        toast.success(`Added ${quantity} ${product.description} to cart`);
        // Navigate to marketplace with cart open
        navigate("/hospital/marketplace?showCart=true");
      } else {
        toast.error("Failed to add to cart");
      }
    } catch (error) {
      toast.error("Failed to add to cart");
    } finally {
      setAddingToCart(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Product Not Found</h2>
          <p className="text-slate-600 mb-4">The product you're looking for doesn't exist.</p>
          <Link to="/hospital/marketplace">
            <Button>Browse All Products</Button>
          </Link>
        </div>
      </div>
    );
  }

  const specs = product.specifications || {};

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link 
              to="/hospital/marketplace" 
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Marketplace</span>
            </Link>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-medium">(800) 988-3892</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          <div className="grid md:grid-cols-2 gap-8 p-8">
            {/* Product Image */}
            <div>
              <div className="relative aspect-square bg-slate-100 rounded-xl overflow-hidden mb-4">
                <img
                  src={product.image_url || "/placeholder-product.png"}
                  alt={product.description}
                  className="w-full h-full object-contain p-8"
                />
                {product.featured && (
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-amber-500 text-white">
                      <Star className="w-3 h-3 mr-1" />
                      Featured
                    </Badge>
                  </div>
                )}
              </div>
              
              {/* Additional Images */}
              {product.additional_images?.length > 0 && (
                <div className="flex gap-2">
                  {product.additional_images.map((img, i) => (
                    <div key={i} className="w-20 h-20 bg-slate-100 rounded-lg overflow-hidden border-2 border-transparent hover:border-emerald-500 cursor-pointer">
                      <img src={img} alt="" className="w-full h-full object-contain p-2" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Product Details */}
            <div>
              <div className="mb-4">
                <Badge className="bg-blue-100 text-blue-800 mb-2">{product.manufacturer}</Badge>
                <p className="text-sm font-mono text-slate-500 mb-1">Item #{product.item_number}</p>
                <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
                  {product.description}
                </h1>
                <p className="text-slate-600 leading-relaxed">
                  {product.long_description || "Premium quality medical supply for healthcare facilities."}
                </p>
              </div>

              {/* Price & Availability */}
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 mb-6 border border-emerald-200">
                <div className="flex items-end gap-2 mb-3">
                  <span className="text-4xl font-bold text-emerald-700">
                    ${product.customer_price?.toFixed(2)}
                  </span>
                  <span className="text-slate-500 mb-1">/ {product.uom || "case"}</span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-emerald-600" />
                    <span><strong>{product.case_pack || product.units_per_case}</strong> units per case</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-emerald-600" />
                    <span><strong>{product.lead_time_text || `${product.lead_time_days} days`}</strong> lead time</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Truck className="w-4 h-4 text-emerald-600" />
                    <span>{product.in_stock ? <span className="text-emerald-700 font-medium">In Stock</span> : "Made to Order"}</span>
                  </div>
                  {product.country_of_origin && (
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-blue-600" />
                      <span className="text-blue-700 font-medium">Product of {product.country_of_origin}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Quantity & Add to Cart */}
              <div className="flex gap-4 mb-6">
                <div className="flex items-center border-2 rounded-lg">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="p-3 hover:bg-slate-100 transition-colors"
                  >
                    <Minus className="w-5 h-5" />
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-16 text-center text-lg font-bold border-0 focus:ring-0"
                    min="1"
                  />
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="p-3 hover:bg-slate-100 transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                <Button
                  onClick={handleAddToCart}
                  disabled={addingToCart}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-lg py-6"
                  data-testid="add-to-cart-btn"
                >
                  {addingToCart ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <ShoppingCart className="w-5 h-5 mr-2" />
                  )}
                  Add to Cart - ${(product.customer_price * quantity).toFixed(2)}
                </Button>
              </div>

              {/* Quick Actions */}
              <div className="flex gap-3 mb-6">
                {product.tech_sheet_url && (
                  <a
                    href={product.tech_sheet_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-lg text-sm text-slate-700 hover:bg-slate-200 transition-colors"
                  >
                    <FileText className="w-4 h-4" />
                    Technical Data Sheet
                  </a>
                )}
                {product.vendor_url && (
                  <a
                    href={product.vendor_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-lg text-sm text-slate-700 hover:bg-slate-200 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Manufacturer Info
                  </a>
                )}
              </div>

              {/* Specifications */}
              {Object.keys(specs).length > 0 && (
                <div className="border-t pt-6">
                  <h3 className="font-bold text-slate-800 mb-4">Specifications</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(specs).map(([key, value]) => (
                      <div key={key} className="flex justify-between py-2 border-b border-slate-100">
                        <span className="text-slate-500 text-sm">{key}</span>
                        <span className="text-slate-800 text-sm font-medium">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Contact Banner */}
        <div className="mt-8 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white text-center">
          <h3 className="text-xl font-bold mb-2">Need Help With Your Order?</h3>
          <p className="text-blue-100 mb-4">Our team is ready to assist you with bulk orders, custom quotes, or any questions.</p>
          <div className="flex items-center justify-center gap-6">
            <a href="tel:8009883892" className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg hover:bg-white/30 transition-colors">
              <Phone className="w-4 h-4" />
              (800) 988-3892
            </a>
            <a href="mailto:orders@sterilemate.com" className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg hover:bg-white/30 transition-colors">
              <FileText className="w-4 h-4" />
              orders@sterilemate.com
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-slate-900 text-white py-6 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-slate-400 text-sm">
            <strong className="text-white">Sterile Mate LLC</strong> | Powered by <strong className="text-blue-400">AskIsaac Intelligence</strong>
          </p>
        </div>
      </div>
    </div>
  );
}
